import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/layout/Header';
import ProfileInfoCard from '../../components/profile/ProfileInfoCard';
import SecuritySection from '../../components/profile/SecuritySection';
import PromoCodeSection from '../../components/profile/PromoCodeSection';
import DeleteAccountModal from '../../components/profile/DeleteAccountModal';
import { ArrowLeft, LogOut, Loader2 } from 'lucide-react';

function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, updateProfile, refreshPromoCodeStatus, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [promoCodeMessage, setPromoCodeMessage] = useState({ type: '', text: '' });
  const [activePromoCodes, setActivePromoCodes] = useState([]);
  const [showPromoCodeAlert, setShowPromoCodeAlert] = useState(false);
  
  // location.state에서 needPromoCode 확인
  useEffect(() => {
    if (location.state?.needPromoCode) {
      setShowPromoCodeAlert(true);
      // state 초기화 (뒤로가기 후 다시 오면 안보이게)
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [linkEmailPassword, setLinkEmailPassword] = useState(false);
  const [emailPasswordData, setEmailPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [providers, setProviders] = useState([]);
  const [hasPassword, setHasPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    emailPassword: false,
    emailConfirm: false
  });
  const [showPromoRequest, setShowPromoRequest] = useState(false);
  const [promoRequestLoading, setPromoRequestLoading] = useState(false);
  const [promoRequestMessage, setPromoRequestMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [promoRequestStatus, setPromoRequestStatus] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [totalPromoUsers, setTotalPromoUsers] = useState(26); // 프로모션 사용자 수
  
  // 컴포넌트 마운트 시 localStorage에서 비밀번호 상태 확인
  useEffect(() => {
    const savedPasswordStatus = localStorage.getItem(`hasPassword_${user?.id}`);
    if (savedPasswordStatus === 'true') {
      setHasPassword(true);
    }
  }, [user?.id]);

  // 컴포넌트 마운트 시 프로모션 사용자 수 즉시 로드
  useEffect(() => {
    loadTotalPromoUsers();
  }, []);

  useEffect(() => {
    if (profile) {
      loadActivePromoCodes();
      loadProviders();
      checkPasswordStatus();
      checkPromoRequestStatus();
      loadTotalPromoUsers();
    }
  }, [profile]);

  useEffect(() => {
    // providers가 업데이트될 때마다 비밀번호 상태 확인
    checkPasswordStatus();
  }, [providers]);

  const loadProviders = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser?.identities) {
        const uniqueProviders = [...new Set(currentUser.identities.map(id => id.provider))];
        setProviders(uniqueProviders);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const checkPasswordStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        // 1. app_metadata 확인
        const userMetadata = session.user.app_metadata;
        const metadataProviders = userMetadata?.providers || [];
        
        // 2. user_metadata 확인
        const userProviders = session.user.user_metadata?.providers || [];
        
        // 3. identities 확인  
        const identities = session.user.identities || [];
        const hasEmailIdentity = identities.some(id => id.provider === 'email');
        
        // 4. 현재 세션의 provider 확인
        const currentProvider = session.user.app_metadata?.provider;
        
        // 어떤 방법으로든 email provider가 확인되면 비밀번호가 있다고 판단
        if (metadataProviders.includes('email') || 
            userProviders.includes('email') || 
            hasEmailIdentity || 
            currentProvider === 'email' ||
            providers.includes('email')) {
          setHasPassword(true);
          console.log('Password detected through various checks');
        }
      }
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };

  const checkPromoRequestStatus = async () => {
    try {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('promo_code_requests')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // single() 대신 maybeSingle() 사용 - 데이터가 없어도 에러 발생하지 않음
        
      if (data) {
        setPromoRequestStatus(data.status);
        
        // 거절된 경우, 그냥 없는 것처럼 처리하여 다시 요청할 수 있게 함
        if (data.status === 'rejected') {
          // UI상에서는 거절 상태를 보여주지만, 
          // 사용자가 다시 요청할 수 있도록 함
          // PromoCodeSection에서 'rejected' 상태일 때도 요청 버튼을 보여줌
        }
      }
    } catch (error) {
      // 요청이 없는 경우는 정상 상태
      console.debug('No promo request found:', error);
    }
  };

  const loadTotalPromoUsers = async () => {
    try {
      // RPC 함수를 사용하여 전체 프로모션 사용자 수 가져오기
      const { data, error } = await supabase.rpc('get_total_promo_users');
      
      console.log('Total promo users count:', data);
      console.log('Total promo users error:', error);
      
      if (!error && data !== null) {
        setTotalPromoUsers(data || 26); // 0이면 기본값 26 사용
      } else {
        // 에러나 데이터가 없으면 기본값 설정
        setTotalPromoUsers(26);
      }
    } catch (error) {
      console.error('Failed to load total promo users:', error);
      setTotalPromoUsers(26); // 에러 시 기본값 설정
    }
  };

  const loadActivePromoCodes = async () => {
    try {
      // 먼저 user_id가 있는지 확인
      if (!user?.id) {
        return;
      }

      // 먼저 모든 프로모 코드 사용 내역을 가져옴 (is_active 상태에 관계없이)
      const { data, error } = await supabase
        .from('promo_code_usage')
        .select(`
          *,
          promo_codes (
            code,
            type,
            benefit_duration_days,
            description,
            is_active
          )
        `)
        .eq('user_id', user.id)
        .order('expires_at', { ascending: false });

      if (!error && data) {
        // 활성 상태이고 만료되지 않은 프로모 코드만 필터링
        const activeAndValidCodes = data.filter(code => {
          // promo_code_usage와 promo_codes 모두 활성 상태여야 함
          if (!code.is_active || !code.promo_codes?.is_active) return false;
          
          // 만료일이 없거나 아직 만료되지 않은 경우만
          if (!code.expires_at) return true;
          return new Date(code.expires_at) > new Date();
        });
        
        setActivePromoCodes(activeAndValidCodes);
        
        // 비활성화되거나 만료된 코드가 있었다면 메시지 표시
        if (data.length > 0 && activeAndValidCodes.length === 0) {
          const hasInactiveCode = data.some(code => !code.promo_codes?.is_active);
          const hasExpiredCode = data.some(code => code.expires_at && new Date(code.expires_at) <= new Date());
          
          let message = '';
          if (hasInactiveCode) {
            message = '이전에 사용하던 프로모션 코드가 비활성화되었습니다.';
          } else if (hasExpiredCode) {
            message = '이전에 사용하던 프로모션 코드가 만료되었습니다.';
          }
          
          if (message) {
            setPromoCodeMessage({ 
              type: 'info', 
              text: message + ' 새로운 코드를 입력해주세요.' 
            });
          }
        }
      } else {
        // 에러가 없고 데이터가 빈 배열인 경우
        setActivePromoCodes([]);
      }
    } catch (error) {
      // 조용히 실패 - 프로모 코드는 선택사항이므로 에러를 사용자에게 보여주지 않음
      console.debug('Failed to load promo codes:', error);
      setActivePromoCodes([]);
    }
  };

  const handlePromoCodeSubmit = async () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeMessage({ type: 'error', text: '프로모션 코드를 입력해주세요.' });
      return;
    }

    setPromoCodeLoading(true);
    setPromoCodeMessage({ type: '', text: '' });

    try {
      const { data, error } = await supabase.rpc('use_promo_code', {
        p_code: promoCodeInput.trim().toUpperCase(),
        p_user_id: user.id
      });

      if (error) throw error;

      // use_promo_code는 배열로 반환되므로 첫 번째 요소를 사용
      const result = Array.isArray(data) ? data[0] : data;
      
      if (result && result.success) {
        setPromoCodeMessage({ type: 'success', text: result.message });
        setPromoCodeInput('');
        await loadActivePromoCodes();
        
        // 프로필 업데이트 (포트폴리오 권한 반영)
        await updateProfile();
        
        // AuthContext의 hasPromoCode 상태 업데이트
        await refreshPromoCodeStatus();
        
        // 프로모션 코드 필요 알림 숨기기
        setShowPromoCodeAlert(false);
        
        // 프로모션 코드가 성공적으로 적용되면 대시보드로 이동
        setTimeout(() => {
          // 강제로 새로고침하여 상태가 확실히 반영되도록 함
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        setPromoCodeMessage({ type: 'error', text: result?.message || '프로모션 코드 적용에 실패했습니다.' });
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
      setPromoCodeMessage({ 
        type: 'error', 
        text: error.message || '프로모션 코드 적용 중 오류가 발생했습니다.' 
      });
    } finally {
      setPromoCodeLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePasswordChange = async () => {
    setPasswordMessage({ type: '', text: '' });
    
    // 유효성 검사
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '모든 필드를 입력해주세요.' });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: '새 비밀번호는 최소 6자 이상이어야 합니다.' });
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordMessage({ type: 'error', text: '새 비밀번호는 현재 비밀번호와 달라야 합니다.' });
      return;
    }
    
    setPasswordChangeLoading(true);
    
    try {
      // 먼저 현재 비밀번호로 재인증
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword
      });
      
      if (signInError) {
        setPasswordMessage({ type: 'error', text: '현재 비밀번호가 올바르지 않습니다.' });
        setPasswordChangeLoading(false);
        return;
      }
      
      // 비밀번호 변경
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });
      
      if (error) throw error;
      
      setPasswordMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordMessage({ 
        type: 'error', 
        text: error.message || '비밀번호 변경 중 오류가 발생했습니다.' 
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // 로그인 방법 확인 (OAuth provider)
  const getLoginMethod = () => {
    const identities = user?.identities || [];
    if (identities.length > 0) {
      const provider = identities[0].provider;
      switch(provider) {
        case 'google':
          return 'Google';
        case 'github':
          return 'GitHub';
        case 'kakao':
          return 'Kakao';
        case 'email':
          return '이메일';
        default:
          return provider;
      }
    }
    return '이메일';
  };

  const hasEmailProvider = () => {
    return providers.includes('email') || hasPassword;
  };

  const handlePromoRequest = async (formData) => {
    setPromoRequestLoading(true);
    setPromoRequestMessage({ type: '', text: '' });

    try {
      // 사용 이유 문자열 생성
      let usageReasonsStr = formData.usageReasons
        .map(reason => {
          if (reason === '기타') {
            return `기타: ${formData.otherReason}`;
          }
          return reason;
        })
        .join(', ');
      
      // 알게 된 경로 문자열 생성
      let discoveryPathStr = formData.discoveryPath;
      if (formData.discoveryPath === '기타') {
        discoveryPathStr = `기타: ${formData.otherPath}`;
      }

      // 먼저 기존 요청이 있는지 확인
      const { data: existingRequest, error: checkError } = await supabase
        .from('promo_code_requests')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // single() 대신 maybeSingle() 사용

      if (existingRequest) {
        // 기존 요청이 있을 경우
        if (existingRequest.status === 'pending') {
          setPromoRequestMessage({ 
            type: 'error', 
            text: '이미 프로모션 코드를 요청하셨습니다. 관리자 승인을 기다려주세요.' 
          });
          return;
        } else if (existingRequest.status === 'rejected') {
          // 거절된 경우, 새로운 정보로 업데이트
          const { error: updateError } = await supabase
            .from('promo_code_requests')
            .update({
              status: 'pending',
              requested_at: new Date().toISOString(),
              metadata: {
                gender: formData.gender,
                age_group: formData.ageGroup,
                usage_reasons: usageReasonsStr,
                discovery_path: discoveryPathStr
              },
              admin_notes: null,
              rejected_at: null
            })
            .eq('id', existingRequest.id);

          if (updateError) throw updateError;

          setPromoRequestMessage({ 
            type: 'success', 
            text: '프로모션 코드 재요청이 성공적으로 전송되었습니다. 승인 후 이메일로 코드를 받으실 수 있습니다.' 
          });
          setShowPromoRequest(false);
          setPromoRequestStatus('pending');
          
          // Google Ads 전환 추적을 위한 URL 파라미터 추가
          const newUrl = new URL(window.location);
          newUrl.searchParams.set('promo_requested', 'true');
          window.history.pushState({}, '', newUrl);
          
          // Google Ads 전환 이벤트 트리거 (재요청도 추적)
          if (typeof window.gtag !== 'undefined') {
            try {
              // 테스트를 위한 고유 트랜잭션 ID 생성
              const transactionId = `promo_${Date.now()}_${user.id}`;
              
              // Google Ads 전환 이벤트
              window.gtag('event', 'conversion', {
                'send_to': 'AW-17348870922/0Z2kCNOU-4EbEIqGy9BA',
                'value': 1.0,
                'currency': 'KRW',
                'transaction_id': transactionId
              });
              
              // 디버그 정보
              console.log('🎯 Google Ads 전환 이벤트 전송:', {
                send_to: 'AW-17348870922/0Z2kCNOU-4EbEIqGy9BA',
                value: 1.0,
                currency: 'KRW',
                transaction_id: transactionId,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                referrer: document.referrer
              });
              
              // dataLayer 확인
              console.log('📊 DataLayer 상태:', window.dataLayer.length, '개 이벤트');
              console.log('🔗 현재 URL:', window.location.href);
              
              // 추가 이벤트 전송 (GA4용)
              window.gtag('event', 'promo_request_submitted', {
                'event_category': 'engagement',
                'event_label': 'promo_request_retry',
                'value': 1,
                'user_id': user.id
              });
              
              console.log('Google Ads 전환 이벤트 전송 성공 (재요청)');
            } catch (error) {
              console.error('Google Ads 전환 이벤트 전송 실패:', error);
            }
          } else {
            console.warn('⚠️ window.gtag가 정의되지 않음');
          }
        } else if (existingRequest.status === 'approved') {
          setPromoRequestMessage({ 
            type: 'info', 
            text: '이미 프로모션 코드가 승인되었습니다. 이메일을 확인해주세요.' 
          });
        }
      } else {
        // 기존 요청이 없을 경우, 새로 삽입
        const { data, error } = await supabase
          .from('promo_code_requests')
          .insert([
            {
              user_id: user.id,
              email: user.email,
              status: 'pending',
              requested_at: new Date().toISOString(),
              metadata: {
                gender: formData.gender,
                age_group: formData.ageGroup,
                usage_reasons: usageReasonsStr,
                discovery_path: discoveryPathStr
              }
            }
          ])
          .select()
          .maybeSingle(); // single() 대신 maybeSingle() 사용

        if (error) {
          throw error;
        } else {
          setPromoRequestMessage({ 
            type: 'success', 
            text: '프로모션 코드 요청이 성공적으로 전송되었습니다. 승인 후 이메일로 코드를 받으실 수 있습니다.' 
          });
          setShowPromoRequest(false);
          setPromoRequestStatus('pending');
          
          // Google Ads 전환 추적을 위한 URL 파라미터 추가
          const newUrl = new URL(window.location);
          newUrl.searchParams.set('promo_requested', 'true');
          window.history.pushState({}, '', newUrl);
          
          // Google Ads 전환 이벤트 트리거
          if (typeof window.gtag !== 'undefined') {
            try {
              // 테스트를 위한 고유 트랜잭션 ID 생성
              const transactionId = `promo_${Date.now()}_${user.id}`;
              
              // Google Ads 전환 이벤트
              window.gtag('event', 'conversion', {
                'send_to': 'AW-17348870922/0Z2kCNOU-4EbEIqGy9BA',
                'value': 1.0,
                'currency': 'KRW',
                'transaction_id': transactionId
              });
              
              // 디버그 정보
              console.log('🎯 Google Ads 전환 이벤트 전송:', {
                send_to: 'AW-17348870922/0Z2kCNOU-4EbEIqGy9BA',
                value: 1.0,
                currency: 'KRW',
                transaction_id: transactionId,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                referrer: document.referrer
              });
              
              // dataLayer 확인
              console.log('📊 DataLayer 상태:', window.dataLayer.length, '개 이벤트');
              console.log('🔗 현재 URL:', window.location.href);
              
              // 추가 이벤트 전송 (GA4용)
              window.gtag('event', 'promo_request_submitted', {
                'event_category': 'engagement',
                'event_label': 'promo_request_new',
                'value': 1,
                'user_id': user.id
              });
              
              console.log('Google Ads 전환 이벤트 전송 성공 (신규 요청)');
            } catch (error) {
              console.error('Google Ads 전환 이벤트 전송 실패:', error);
            }
          } else {
            console.warn('⚠️ window.gtag가 정의되지 않음');
          }
        }
      }
    } catch (error) {
      console.error('Error requesting promo code:', error);
      setPromoRequestMessage({ 
        type: 'error', 
        text: '요청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' 
      });
    } finally {
      setPromoRequestLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== '계정을 삭제합니다') {
      return;
    }

    setDeleteLoading(true);

    try {
      // 1. 먼저 데이터 익명화 처리
      const { data: deleteResult, error: deleteError } = await supabase
        .rpc('delete_user_account', {
          p_user_id: user.id
        });

      if (deleteError) throw deleteError;

      // 2. 로그아웃 수행 (일반 사용자는 auth 삭제 권한이 없음)
      // Auth 사용자 삭제는 관리자가 별도로 처리해야 함
      await supabase.auth.signOut();

      // 3. 로컬 스토리지 정리
      localStorage.clear();
      
      // 4. 로그인 페이지로 리다이렉트
      navigate('/login', { 
        state: { 
          message: '계정이 성공적으로 삭제되었습니다. 그동안 이용해주셔서 감사합니다.' 
        } 
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('계정 삭제 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLinkEmailPassword = async () => {
    setPasswordMessage({ type: '', text: '' });
    
    // 유효성 검사
    if (!emailPasswordData.password || !emailPasswordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '모든 필드를 입력해주세요.' });
      return;
    }
    
    if (emailPasswordData.password.length < 6) {
      setPasswordMessage({ type: 'error', text: '비밀번호는 최소 6자 이상이어야 합니다.' });
      return;
    }
    
    if (emailPasswordData.password !== emailPasswordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '비밀번호가 일치하지 않습니다.' });
      return;
    }
    
    setPasswordChangeLoading(true);
    
    try {
      // 이메일/비밀번호 인증 추가
      const { error } = await supabase.auth.updateUser({
        password: emailPasswordData.password
      });
      
      if (error) throw error;
      
      setPasswordMessage({ type: 'success', text: '이메일/비밀번호 로그인이 추가되었습니다. 이제 이메일과 비밀번호로도 로그인할 수 있습니다.' });
      setEmailPasswordData({ password: '', confirmPassword: '' });
      setLinkEmailPassword(false);
      
      // 프로바이더 목록 다시 로드
      await loadProviders();
      
      // 강제로 비밀번호 상태를 true로 설정
      setHasPassword(true);
      
      // localStorage에 저장
      localStorage.setItem(`hasPassword_${user.id}`, 'true');
      
      // 세션 새로고침
      const { data: { session } } = await supabase.auth.refreshSession();
      if (session) {
        console.log('Session refreshed after password set:', session);
        // 세션 새로고침 후 다시 확인
        setTimeout(() => {
          checkPasswordStatus();
        }, 500);
      }
    } catch (error) {
      console.error('Error linking email/password:', error);
      setPasswordMessage({ 
        type: 'error', 
        text: error.message || '이메일/비밀번호 연결 중 오류가 발생했습니다.' 
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const result = await signOut();
      
      if (!result.success && result.error) {
        console.warn('로그아웃 중 경고:', result.error);
      }
      // 로그아웃 성공 시 자동으로 AuthContext에서 상태가 변경되어 로그인 페이지로 리다이렉트됨
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="page-container">
      <Header title="내 프로필" />
      
      <div className="page-content-lg">
        {/* 뒤로가기 버튼 */}
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost btn-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          뒤로가기
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 왼쪽: 프로필 정보 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 기본 정보 카드 */}
            <ProfileInfoCard 
              profile={profile}
              user={user}
              formatDate={formatDate}
              handleLogout={handleLogout}
              isLoggingOut={isLoggingOut}
            />
            
            {/* 보안 설정 */}
            <div className="card">
              <SecuritySection
                providers={providers}
                hasEmailProvider={hasEmailProvider}
                showPasswordChange={showPasswordChange}
                setShowPasswordChange={setShowPasswordChange}
                passwordData={passwordData}
                setPasswordData={setPasswordData}
                passwordMessage={passwordMessage}
                passwordChangeLoading={passwordChangeLoading}
                handlePasswordChange={handlePasswordChange}
                linkEmailPassword={linkEmailPassword}
                setLinkEmailPassword={setLinkEmailPassword}
                emailPasswordData={emailPasswordData}
                setEmailPasswordData={setEmailPasswordData}
                setPasswordMessage={setPasswordMessage}
                handleLinkEmailPassword={handleLinkEmailPassword}
                showPasswords={showPasswords}
                setShowPasswords={setShowPasswords}
                user={user}
                setShowDeleteConfirmation={setShowDeleteConfirmation}
              />
            </div>
          </div>

          {/* 오른쪽: 프로모션 코드 입력 */}
          <PromoCodeSection
            showPromoCodeAlert={showPromoCodeAlert}
            setShowPromoCodeAlert={setShowPromoCodeAlert}
            promoCodeInput={promoCodeInput}
            setPromoCodeInput={setPromoCodeInput}
            promoCodeLoading={promoCodeLoading}
            promoCodeMessage={promoCodeMessage}
            handlePromoCodeSubmit={handlePromoCodeSubmit}
            activePromoCodes={activePromoCodes}
            formatDate={formatDate}
            setShowPromoRequest={setShowPromoRequest}
            showPromoRequest={showPromoRequest}
            promoRequestLoading={promoRequestLoading}
            promoRequestMessage={promoRequestMessage}
            setPromoRequestMessage={setPromoRequestMessage}
            handlePromoRequest={handlePromoRequest}
            user={user}
            promoRequestStatus={promoRequestStatus}
            totalPromoUsers={totalPromoUsers}
          />
        </div>

        {/* 탈퇴 확인 모달 */}
        <DeleteAccountModal
          showDeleteConfirmation={showDeleteConfirmation}
          setShowDeleteConfirmation={setShowDeleteConfirmation}
          deleteConfirmText={deleteConfirmText}
          setDeleteConfirmText={setDeleteConfirmText}
          deleteLoading={deleteLoading}
          handleDeleteAccount={handleDeleteAccount}
        />
      </div>
    </div>
  );
}

export default Profile;
